import type { TableData } from '@mantine/core'
import { Alert, Box, Button, Divider, Group, Modal, Skeleton, Stack, Table, Text } from '@mantine/core'
import type { FileWithPath } from '@mantine/dropzone'
import { Dropzone, MIME_TYPES } from '@mantine/dropzone'
import { useDisclosure } from '@mantine/hooks'
import type { Asset, User } from '@opsopti/shared'
import {
  castAssetGroupToForm,
  castAssetToForm,
  castUserToForm,
  downloadDataAsFile,
  getObjectEntries,
  getObjectKeys,
  getObjectValues,
  removeNullishValuesFromObject,
  useAuthorization,
  useGlobalState,
  wait,
} from '@opsopti/shared'
import type { RequiredDataFromCollectionSlug, SafeRequestResponse } from '@opsopti/shared/src/services/api/api.types'
import { IconAlertOctagon, IconTableImport, IconX } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import pluralize from 'pluralize'
import { parse, stringify } from 'qs'
import type { RefObject } from 'react'
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import type { ZodFormattedError } from 'zod'
import { z } from 'zod'

import { theme } from '@/configs/theme'
import * as classes from '@/styles/ImportModal.css'
import { showSuccessNotification } from '@/utils/componentCommons'

import { EmptyState } from './EmptyState'

export type ImportModalRef = ReturnType<typeof useDisclosure>['1'] | null

interface ImportModalProps {
  importModalRef?: RefObject<ImportModalRef>
  schema: keyof typeof schemaMap
}

function ImportModalComponent(props: ImportModalProps) {
  const { importModalRef, schema: schemaString } = props

  const openRef = useRef<() => void>(null)
  const [modalOpen, modalControls] = useDisclosure(false)
  const { api, uiStore } = useGlobalState()
  const [file, setFile] = useState<FileWithPath | null>(null)
  const [data, setData] = useState<ImportedData | null>(null)
  const [validations, setValidations] = useState<ZodFormattedError<any> | null>(null)

  useImperativeHandle(importModalRef, () => ({ ...modalControls }), [])

  useEffect(() => {
    if (file) {
      processFile(file)
    }
  }, [file])

  const entity = entityMap[schemaString]
  const schema = schemaMap[schemaString]
  const defaultData = defaultsMap[schemaString]
  const placeholderTableData = useMemo(() => generatePlaceholderTableData(schemaString), [schemaString])
  const tableData = useMemo(() => generateTableData(schemaString, data, validations), [schemaString, data, validations])
  const uniqueValidation = useMemo(() => {
    const columns = getObjectKeys(schema.shape)
    // @ts-expect-error -- description might not exist
    const uniqueColumns = columns.filter(c => schema.shape[c].description?.includes('unique'))

    const validations = uniqueColumns.map(c => {
      const values = data?.map(d => d[c]) ?? []
      const uniqueCount = new Set(values).size
      return uniqueCount === values.length
    }, {})

    return validations.some(v => !v)
  }, [data, schema])

  async function processFile(file: FileWithPath) {
    const response = await new Promise<{ ok: true; data: ImportedData } | { ok: false; error: Error }>(resolve => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: d => resolve({ ok: true, data: d.data as unknown as ImportedData }),
        error: e => resolve({ ok: false, error: e }),
      })
    })

    if (response.ok) {
      const data = response.data.map(d => ({ ...defaultData, ...removeNullishValuesFromObject(d) }))
      setData(data)

      const zodResponse = z.array(schema).safeParse(data)

      if (!zodResponse.success) {
        setValidations(zodResponse.error.format())
      }
    }
  }

  async function startImport() {
    uiStore.showLoadingOverlay('fullscreen', { helper: 'Importing data. This may take a while...' })

    await wait(2000)

    const totalCount = data?.length ?? 0
    let count = 0
    const failedItems: ImportedData = []
    const failedItemErrors: { message: string; path: string }[][] = []

    for (const item of data ?? []) {
      count++
      uiStore.updateLoadingOverlay({ helper: `Importing ${count} of ${totalCount}` })
      let createResponse: SafeRequestResponse<any> | undefined = undefined

      const keys = getObjectKeys(item)

      const groupedData = keys.reduce(
        (acc, key) => {
          const [model, field] = key.split('.') as [string, string]

          if (!acc[model]) {
            acc[model] = {}
          }

          acc[model][field] = item[key]

          return acc
        },
        {} as Record<string, any>,
      )

      const castedData = getObjectEntries(groupedData).reduce(
        (acc, [model, data]) => {
          // @ts-expect-error -- castFnMap needs a proper index type which we don't have
          acc[model] = castFnMap[model](data)
          return acc
        },
        {} as Record<string, RequiredDataFromCollectionSlug<any>>,
      )

      if (schemaString === 'asset') {
        let assetGroupId: string | null = null

        const findGroup = await api.safeRequest(api.getAssetGroups, {
          where: { name: { equals: castedData.assetGroup.name } },
          limit: '0',
          depth: '0',
        })

        if (findGroup.ok && findGroup.data.docs.length > 0) {
          assetGroupId = findGroup.data.docs[0].id
        }

        if (!assetGroupId) {
          const createGroup = await api.safeRequest(api.createAssetGroup, castedData.assetGroup as any)

          if (createGroup.ok) {
            assetGroupId = createGroup.data.doc.id
          } else {
            failedItems.push(item)
            failedItemErrors.push(
              (createGroup.errors?.[0]?.data?.errors ?? []).map(e => ({
                message: e.message,
                path: `assetGroup.${e.path}`,
              })),
            )
            continue
          }
        }

        createResponse = await api.safeRequest(api.createAsset, {
          ...castedData.asset,
          assetGroup: assetGroupId,
        } as any)
      } else if (schemaString === 'user') {
        createResponse = await api.safeRequest(api.createUser, castedData.user as any)
      }

      if (createResponse) {
        if (createResponse.ok) {
          showSuccessNotification({ title: `${entity} Imported` })
        } else {
          failedItems.push(item)
          failedItemErrors.push(
            (createResponse.errors?.[0]?.data?.errors ?? []).map(e => ({
              message: e.message,
              path: `${schemaString}.${e.path}`,
            })),
          )
        }
      }
    }

    if (!!failedItems.length) {
      setData(failedItems)
      setValidations(apiErrorsToZodErrors(failedItemErrors))
    } else {
      reset()
      api.queryClient.invalidateQueries()
    }

    uiStore.hideLoadingOverlay()
  }

  function reset() {
    setFile(null)
    setData(null)
    setValidations(null)
  }

  return (
    <Modal
      centered
      radius="lg"
      size="100%"
      keepMounted={false}
      closeOnEscape={false}
      opened={modalOpen}
      onClose={() => {
        modalControls.close()
        reset()
      }}
      title={`Import ${pluralize(entity)}`}>
      <Dropzone
        radius="md"
        accept={[MIME_TYPES.csv]}
        maxFiles={1}
        activateOnClick={false}
        mih={600}
        loading={!!file && !data}
        openRef={openRef}
        styles={{
          root: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: !!tableData ? 'flex-start' : 'center',
          },
          inner: { pointerEvents: 'all' },
        }}
        onDrop={f => {
          reset()
          if (f.length > 0) {
            setFile(f[0])
          }
        }}>
        <Dropzone.Idle>
          <Table
            layout="auto"
            horizontalSpacing="md"
            verticalSpacing="md"
            striped
            withColumnBorders
            data={tableData ?? placeholderTableData}
            classNames={classes.table}
          />

          {!!tableData ? (
            <Stack align="center" py="xl">
              {(!!validations || uniqueValidation) && (
                <Box px="md" w="100%">
                  <Alert variant="filled" color="red" title="Issues Found" icon={<IconAlertOctagon />}>
                    One or more items failed validation. Please double check the table above for errors.
                  </Alert>
                </Box>
              )}

              <Group gap="xl">
                <Button
                  disabled={!!validations || uniqueValidation}
                  rightSection={<IconTableImport />}
                  onClick={startImport}>
                  Start Import
                </Button>

                <Button size="sm" radius="sm" variant="light" color="gray" onClick={reset}>
                  Reset
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack align="center" py="xl">
              <Text>Drag & drop CSV file here to upload.</Text>
              <Divider label="or" miw={120} />
              <Group gap="xl">
                <Button miw={200} onClick={() => openRef.current?.()}>
                  Select CSV File
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  miw={200}
                  onClick={() => {
                    const csvTemplate = generateCSVTemplate(schemaString)
                    downloadDataAsFile(csvTemplate, `Ops-Opti-Import-Template-${entity}.csv`, MIME_TYPES.csv)
                  }}>
                  Download CSV Template
                </Button>
              </Group>
            </Stack>
          )}
        </Dropzone.Idle>

        <Dropzone.Reject>
          <EmptyState
            size="sm"
            title={`Unsupported File Type`}
            description={`Only CSV files are supported.`}
            Icon={IconX}
            IconProps={{ color: theme.colors.error }}
            TitleProps={{ c: theme.colors.error }}
            DescriptionProps={{ c: theme.colors.error }}
          />
        </Dropzone.Reject>
      </Dropzone>
    </Modal>
  )
}

export const ImportModal = observer(function ImportModal(props: ImportModalProps) {
  const importEnabled = useAuthorization({ FlagAllowImport: true })

  if (importEnabled) {
    return <ImportModalComponent {...props} />
  } else {
    return null
  }
})

function generatePlaceholderTableData(schemaString: ImportModalProps['schema']): TableData {
  const schema = schemaMap[schemaString]
  const columns = getObjectKeys(schema.shape)

  return {
    head: columns,
    body: Array.from({ length: 7 }, () =>
      columns.map(c => <Skeleton width={skeletonWidthMap[c]} height={8} key={c} />),
    ),
    foot: columns.map(c => {
      // @ts-expect-error -- description might not exist
      const tags = (schema.shape[c].description ?? '').split(',') as SchemaTag[]

      return (
        <Stack key={c} gap="xs" justify="flex-start">
          {tags.map(tag => {
            if (tag === 'required') {
              return (
                <Text key={tag} size="xs" fw={700} c="red.8" tt="uppercase" lts={0.2}>
                  Required
                </Text>
              )
            } else if (tag === 'unique') {
              return (
                <Text key={tag} size="xs" fw={700} c="orange.8" tt="uppercase" lts={0.2}>
                  Unique
                </Text>
              )
            } else if (tag === 'optional') {
              return (
                <Text key={tag} size="xs" fw={700} c="dimmed" tt="uppercase" lts={0.2}>
                  Optional
                </Text>
              )
            } else if (tag === 'oneof') {
              return (
                <Text key={tag} size="xs" fw={700} c="blue.8" tt="uppercase" lts={0.2}>
                  One Of:
                  {getObjectValues(
                    // @ts-expect-error -- enum might not exist
                    schema.shape[c]?.enum ?? schema.shape[c]?._def?.innerType?.enum ?? [],
                  ).map(e => (
                    <Text span display="block" key={e} c="blue.8" fw={700} tt="none" size="sm">
                      - {e}
                    </Text>
                  ))}
                </Text>
              )
            } else if (tag.startsWith('notes')) {
              return (
                <Text key={tag} size="xs" fw={700} c="yellow.8" tt="uppercase" lts={0.2} maw={200}>
                  Notes:
                  <Text span display="block" c="yellow.8" tt="none" size="sm">
                    {(parse(tag)?.notes ?? '') as string}
                  </Text>
                </Text>
              )
            }
          })}
        </Stack>
      )
    }),
  }
}

function generateTableData(
  schemaString: ImportModalProps['schema'],
  data: ImportedData | null,
  validations: ZodFormattedError<any> | null,
): TableData | null {
  if (!data) {
    return null
  }

  const schema = schemaMap[schemaString]
  const columns = getObjectKeys(schema.shape)
  // @ts-expect-error -- description might not exist
  const uniqueColumns = columns.filter(c => schema.shape[c].description?.includes('unique'))

  return {
    head: columns,
    body: data.map((d, i) => {
      // @ts-expect-error -- validation type is not known
      const rowValidations = validations?.[i]

      return getObjectEntries(d).map(([key, value]) => {
        const cellValidation = rowValidations?.[key]?._errors?.join(', ') ?? null
        let uniqueValidation = false

        if (
          // @ts-expect-error -- column is never
          uniqueColumns.includes(key)
        ) {
          const previousValues = data.slice(0, i).map(d => d[key])

          if (previousValues.includes(value)) {
            uniqueValidation = true
          }
        }

        return (
          <>
            <Text key={key} className={theme.cx((cellValidation || uniqueValidation) && 'import-cell-error')}>
              {value}&nbsp;
              {(!!cellValidation || uniqueValidation) && (
                <Text size="sm" fw={700} c="red.9">
                  {uniqueValidation ? 'must be unique' : cellValidation}
                </Text>
              )}
            </Text>
          </>
        )
      })
    }),
  }
}

function apiErrorsToZodErrors(errors: { message: string; path: string }[][]): ZodFormattedError<any> {
  return errors.reduce((acc, err, i) => {
    const e = err[0]

    // @ts-expect-error -- i is wrong index signature
    acc[String(i)] = {
      _errors: [],
      [e?.path ?? '_']: {
        _errors: [e?.message ?? 'unknown error'],
      },
    }

    return acc
  }, {} as ZodFormattedError<any>)
}

function generateCSVTemplate(schemaString: ImportModalProps['schema']): string {
  const schema = schemaMap[schemaString]
  const columns = getObjectKeys(schema.shape)

  return Papa.unparse({ fields: columns, data: Array.from({ length: 5 }, () => columns.map(() => '')) })
}

type ImportedData = Record<string, string | null>[]

const AssetImportSchema = z.object({
  'assetGroup.name': z.string().min(1, "can't be empty").describe('required'),
  'asset.name': z.string().min(1, "can't be empty").describe('required'),
  'asset.identifier': z.string().min(1, "can't be empty").describe('required,unique'),
  'asset.visibilityStatus': z
    .enum(['active', 'inactive', 'archived'] as const satisfies Asset['visibilityStatus'][], {
      message: 'must be one of: active, inactive, archived',
    })
    .describe('optional,oneof'),
  'asset.internalNotes': z.string().nullish().describe('optional'),
  'asset.memberNotes': z.string().nullish().describe('optional'),
})

const UserImportSchema = z.object({
  'user.firstName': z.string().min(1, "can't be empty").describe('required'),
  'user.lastName': z.string().min(1, "can't be empty").describe('required'),
  'user.email': z.string().email('must be an email').min(1, "can't be empty").describe('required,unique'),
  'user.role': z
    .enum(['admin', 'member', 'supervisor'] as const satisfies User['role'][], {
      message: 'must be one of: admin, member, supervisor',
    })
    .describe('optional,oneof'),
  'user.accountStatus': z
    .enum(['enabled', 'disabled'] as const satisfies User['accountStatus'][], {
      message: 'must be one of: enabled, disabled',
    })
    .describe(
      `optional,oneof,${stringify({ notes: 'If `enabled`, the user will receive an email to set their password. Set to `disabled` to prevent this.' })}`,
    ),
  'user.visibilityStatus': z
    .enum(['internal', 'public', 'archived'] as const satisfies User['visibilityStatus'][], {
      message: 'must be one of: internal, public, archived',
    })
    .describe('optional,oneof'),
  'user.internalNotes': z.string().nullish().describe('optional'),
  'user.memberNotes': z.string().nullish().describe('optional'),
})

type AssetImportSchema = z.infer<typeof AssetImportSchema>
type UserImportSchema = z.infer<typeof UserImportSchema>

const defaultsMap: { asset: Partial<AssetImportSchema>; user: Partial<UserImportSchema> } = {
  asset: {
    'assetGroup.name': '',
    'asset.name': '',
    'asset.identifier': '',
    'asset.visibilityStatus': 'active',
    'asset.internalNotes': null,
    'asset.memberNotes': null,
  },
  user: {
    'user.firstName': '',
    'user.lastName': '',
    'user.email': '',
    'user.role': 'member',
    'user.accountStatus': 'enabled',
    'user.visibilityStatus': 'public',
    'user.internalNotes': null,
    'user.memberNotes': null,
  },
}

const schemaMap = {
  asset: AssetImportSchema,
  user: UserImportSchema,
}

const entityMap: Record<keyof typeof schemaMap, string> = {
  asset: 'Asset',
  user: 'User',
}

const skeletonWidthMap: Record<keyof AssetImportSchema & keyof UserImportSchema, number> = {
  'assetGroup.name': 100,
  'asset.name': 150,
  'asset.identifier': 200,
  'asset.visibilityStatus': 80,
  'asset.internalNotes': 200,
  'asset.memberNotes': 200,
  'user.firstName': 100,
  'user.lastName': 100,
  'user.email': 200,
  'user.role': 80,
  'user.accountStatus': 80,
  'user.visibilityStatus': 80,
  'user.internalNotes': 200,
  'user.memberNotes': 200,
}

const castFnMap = {
  asset: castAssetToForm,
  assetGroup: castAssetGroupToForm,
  user: castUserToForm,
}

type SchemaTag = 'required' | 'optional' | 'unique' | 'oneof' | `notes=${string}`
